// Head to head (MATCHUP.md; the H2H artboards, docs/design/mockups/looks/
// H2H-*.dc.html): my starters and theirs paired slot by slot — my player and
// points on the left, the slot between, theirs on the right — with the two
// totals at the foot. The default on a tablet and a desktop, the VS tab on a
// phone (B-209).
//
// PAIRED BY POSITION IN THE LINEUP. Both sides of a matchup play the same
// league's lineup, so the nth starter of each is the same slot (QB against
// QB, FLEX against FLEX); a side short a row pairs with an empty cell rather
// than a guess.
//
// Which side took a slot is marked on the row (`data-take`) so a look can
// light it (Scoreboard washes the winner's half); the plain rule here only
// dims the smaller number.

import { useState } from 'react';
import type { MatchupRowView, MatchupSide } from '@fsp/domain';
import { GameLabel, PositionPill, ProTeamCode, StatusBadge } from '../../kit';
import { Detail, useAtLeast } from '../../data/detail';
import { num } from './notComputed';
import { TeamName } from './Names';
import { PointsWhy } from './PointsWhy';
import { Term } from '../../kit/Why';
import { Icon } from '../../kit/Icon';
import { GLOSSARY } from './glossary';
import { useTools } from '../../data/tools';

export function HeadToHead({
  me,
  them,
  onOpen,
  short = false,
  final = false,
}: {
  me: MatchupSide;
  them: MatchupSide;
  /** A settled week, whose empty slots and byes counted 0 (B-233). */
  final?: boolean;
  /** Short names ("P. Mahomes"), for a phone's half-width columns. */
  short?: boolean;
  /** A player's name opens his card. */
  onOpen: (row: MatchupRowView) => void;
}) {
  const tools = useTools();
  // The game line under a name and the projection under a number are
  // Standard's, as on the lists (DETAIL_LEVELS §Matchup).
  const context = useAtLeast('standard');
  // THE BENCHES, COLLAPSED UNDER THEIR NAME (MATCHUP.md, B-382): both sides'
  // players paired as the starters are, once opened; an open spot is nobody.
  const [benchOpen, setBenchOpen] = useState(false);
  const myBench = me.bench.filter((r) => r.player !== null);
  const theirBench = them.bench.filter((r) => r.player !== null);
  const benchPairs = Array.from(
    { length: Math.max(myBench.length, theirBench.length) },
    (_, i): [MatchupRowView | null, MatchupRowView | null] => [
      myBench[i] ?? null,
      theirBench[i] ?? null,
    ],
  );
  const n = Math.max(me.rows.length, them.rows.length);
  const pairs = Array.from(
    { length: n },
    (_, i): [MatchupRowView | null, MatchupRowView | null] => [
      me.rows[i] ?? null,
      them.rows[i] ?? null,
    ],
  );

  return (
    <div className="fsp-h2h-board" role="table" aria-label="Head to head">
      <div className="fsp-h2h-row fsp-h2h-head" role="row">
        <span className="fsp-cap" role="columnheader">
          {me.team.isMine ? 'You' : <TeamName team={me.team} />}
        </span>
        <span className="fsp-cap fsp-h2h-slot" role="columnheader">
          Slot
        </span>
        <span className="fsp-cap fsp-h2h-right" role="columnheader">
          <TeamName team={them.team} />
        </span>
      </div>
      {pairs.map(([mine, theirs], i) => {
        const slot = mine?.slot ?? theirs?.slot ?? '';
        const take =
          mine?.points != null && theirs?.points != null && mine.points !== theirs.points
            ? mine.points > theirs.points
              ? 'me'
              : 'them'
            : undefined;
        return (
          <div key={`${slot}-${i}`} className="fsp-h2h-row" role="row" data-take={take}>
            <Cell
              row={mine}
              side="me"
              context={context}
              short={short}
              final={final}
              onOpen={onOpen}
            />
            <span className="fsp-h2h-slot" role="cell">
              <PositionPill pos={slot} size="sm" />
              {/* What the small numbers under the points are, once, between
                  them (the H2H artboards' PROJ). */}
              {context && tools.projections ? (
                <span className="fsp-cap fsp-h2h-proj-label">proj</span>
              ) : null}
            </span>
            <Cell
              row={theirs}
              side="them"
              context={context}
              short={short}
              final={final}
              onOpen={onOpen}
            />
          </div>
        );
      })}
      <div className="fsp-h2h-row fsp-h2h-total" role="row">
        <div className="fsp-h2h-cell" role="cell">
          <span className="fsp-cap">Total</span>
          <span className="fsp-num fsp-h2h-points">{me.score.toFixed(1)}</span>
        </div>
        <span className="fsp-h2h-slot" role="cell" />
        <div className="fsp-h2h-cell fsp-h2h-them" role="cell">
          <span className="fsp-num fsp-h2h-points">{them.score.toFixed(1)}</span>
          <span className="fsp-cap">Total</span>
        </div>
      </div>
      <Detail min="standard">
        <div className="fsp-h2h-row fsp-h2h-proj" role="row">
          <span className="fsp-sub fsp-num" role="cell">
            {tools.projections ? `proj ${num(me.projected)} · ` : ''}
            {me.yetToPlay} to play
          </span>
          <span role="cell" />
          <span className="fsp-sub fsp-num fsp-h2h-right" role="cell">
            {them.yetToPlay} to play
            {tools.projections ? ` · proj ${num(them.projected)}` : ''}
          </span>
        </div>
      </Detail>
      {benchPairs.length > 0 ? (
        <>
          {/* A ROW OF ITS OWN, so the table's children are all rows (axe's
              aria-required-children): the button sat straight in the table. */}
          <div className="fsp-h2h-toggle-row" role="row">
            <div role="cell">
              <button
                type="button"
                className="fsp-h2h-bench-toggle"
                aria-expanded={benchOpen}
                onClick={() => setBenchOpen((o) => !o)}
              >
                <Icon name={benchOpen ? 'expand' : 'next'} size="sm" />
                <span className="fsp-cap">Bench</span>
              </button>
            </div>
          </div>
          {benchOpen
            ? benchPairs.map(([mine, theirs], i) => (
                <div key={`bench-${i}`} className="fsp-h2h-row fsp-h2h-bench" role="row">
                  <Cell
                    row={mine}
                    side="me"
                    context={context}
                    short={short}
                    final={final}
                    benched
                    onOpen={onOpen}
                  />
                  <span className="fsp-h2h-slot" role="cell">
                    <PositionPill pos={mine?.slot ?? theirs?.slot ?? 'BN'} size="sm" />
                  </span>
                  <Cell
                    row={theirs}
                    side="them"
                    context={context}
                    short={short}
                    final={final}
                    benched
                    onOpen={onOpen}
                  />
                </div>
              ))
            : null}
        </>
      ) : null}
      {/* The bench comparison is Everything's (DETAIL_LEVELS §Matchup). */}
      <Detail min="pro">
        <div className="fsp-h2h-row fsp-h2h-proj" role="row">
          <span className="fsp-sub" role="cell">
            <Term tip={GLOSSARY.bench}>Bench</Term>{' '}
            <span className="fsp-num">{num(me.benchPoints)}</span>
          </span>
          <span role="cell" />
          <span className="fsp-sub fsp-h2h-right" role="cell">
            <Term tip={GLOSSARY.bench}>Bench</Term>{' '}
            <span className="fsp-num">{num(them.benchPoints)}</span>
          </span>
        </div>
      </Detail>
    </div>
  );
}

function Cell({
  row,
  side,
  context,
  short,
  final,
  benched = false,
  onOpen,
}: {
  row: MatchupRowView | null;
  side: 'me' | 'them';
  context: boolean;
  short: boolean;
  final: boolean;
  /** A bench row: not in the score, so a settled week's bye is not a 0 it holds. */
  benched?: boolean;
  onOpen: (row: MatchupRowView) => void;
}) {
  const tools = useTools();
  const cls = `fsp-h2h-cell ${side === 'them' ? 'fsp-h2h-them' : ''}`;
  if (row === null || row.player === null) {
    // A settled week counted the empty slot as 0, and says so (B-233).
    const zero =
      row !== null && final && !benched ? (
        <span className="fsp-h2h-score">
          <span className="fsp-num fsp-h2h-points">{(0).toFixed(1)}</span>
        </span>
      ) : null;
    return (
      <div className={cls} role="cell">
        {side === 'them' ? zero : null}
        <span className="fsp-muted">Empty</span>
        {side === 'me' ? zero : null}
      </div>
    );
  }
  const player = row.player;
  const who = (
    <button type="button" className="fsp-h2h-who fsp-linklike" onClick={() => onOpen(row)}>
      <span className="fsp-name">
        <span className="fsp-name-text">{short ? player.shortName : player.name}</span>
        <span className="fsp-name-marks">
          <ProTeamCode code={player.proTeam} />
          {/* A BYE ONCE (B-521): the game's line says it where it shows. */}
          <StatusBadge player={context ? { ...player, onBye: false } : player} />
        </span>
      </span>
      {/* A game that came and went without him, at every level (B-233). */}
      {row.noScore === 'did-not-play' ? (
        <span className="fsp-sub fsp-h2h-line fsp-matchup-noscore">
          Did not play
          {context ? (
            <>
              {' · '}
              <GameLabel game={row.game} />
            </>
          ) : null}
        </span>
      ) : context ? (
        <span className="fsp-sub fsp-h2h-line">
          {row.statLine ?? <GameLabel game={row.game} />}
        </span>
      ) : null}
    </button>
  );
  // Points once his line is in; 0 for a settled week's bye; a dash for a game
  // under way without it; and nothing at Simple before kickoff, as on the
  // lists (B-172, B-233), nor where no projection sits under it (§15c).
  const points =
    row.points !== null
      ? row.points.toFixed(1)
      : final && row.noScore !== null && !benched
        ? (0).toFixed(1)
        : (!context || !tools.projections) && row.game?.state === 'pre'
          ? ''
          : '—';
  const score = (
    <span className="fsp-h2h-score">
      <span className={`fsp-num fsp-h2h-points ${row.points !== null ? 'scored' : ''}`}>
        {row.points !== null ? (
          <PointsWhy
            player={player}
            points={row.points}
            breakdown={row.breakdown}
            statLine={row.statLine}
          >
            {points}
          </PointsWhy>
        ) : (
          points
        )}
      </span>
      {context && tools.projections ? (
        <span className="fsp-sub fsp-num">{num(row.projection)}</span>
      ) : null}
    </span>
  );
  return (
    <div className={cls} role="cell">
      {side === 'me' ? (
        <>
          {who}
          {score}
        </>
      ) : (
        <>
          {score}
          {who}
        </>
      )}
    </div>
  );
}
